/* ============================================================
   Лёгкие SVG-графики без внешних зависимостей.
   Стилизованы под дизайн-токены приложения.
   ============================================================ */
import './charts.css';

const COLOR: Record<string, string> = {
  blue: 'var(--blue)',
  green: 'var(--green)',
  yellow: 'var(--yellow)',
  red: 'var(--red)',
  accent: 'var(--accent)',
};

function fmtMoney(v: number): string {
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(1).replace('.', ',') + ' млн';
  if (v >= 1_000) return Math.round(v / 1_000) + ' тыс';
  return String(Math.round(v));
}

// ── Столбчатая диаграмма (динамика по месяцам) ──
export function BarChart({ data, height = 200 }: {
  data: { label: string; value: number }[];
  height?: number;
}) {
  const max = Math.max(...data.map((d) => d.value), 1);
  const W = 100; // проценты по ширине
  const barW = W / data.length;

  return (
    <div className="chart">
      <svg viewBox={`0 0 ${W} ${height}`} preserveAspectRatio="none" className="chart__svg" style={{ height }}>
        {/* сетка */}
        {[0.25, 0.5, 0.75, 1].map((g) => (
          <line key={g} x1={0} x2={W} y1={height - g * (height - 24)} y2={height - g * (height - 24)}
            stroke="var(--border)" strokeWidth={0.3} />
        ))}
        {data.map((d, i) => {
          const h = (d.value / max) * (height - 24);
          const x = i * barW + barW * 0.2;
          const w = barW * 0.6;
          return (
            <rect key={i} x={x} y={height - h} width={w} height={h}
              rx={0.6} fill="var(--accent)" opacity={0.85} />
          );
        })}
      </svg>
      <div className="chart__xaxis">
        {data.map((d, i) => (
          <div key={i} className="chart__xtick">
            <span className="chart__xval">{d.value > 0 ? fmtMoney(d.value) : ''}</span>
            <span className="chart__xlabel">{d.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Кольцевая диаграмма (распределение) ──
export function DonutChart({ data, size = 160 }: {
  data: { label: string; value: number; color: string }[];
  size?: number;
}) {
  const total = data.reduce((s, d) => s + d.value, 0);
  const r = size / 2 - 16;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * r;
  let offset = 0;

  return (
    <div className="chart-donut">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--border)" strokeWidth={14} />
        {total > 0 && data.map((d, i) => {
          const frac = d.value / total;
          const dash = frac * circumference;
          const seg = (
            <circle key={i} cx={cx} cy={cy} r={r} fill="none"
              stroke={COLOR[d.color] ?? 'var(--accent)'} strokeWidth={14}
              strokeDasharray={`${dash} ${circumference - dash}`}
              strokeDashoffset={-offset}
              transform={`rotate(-90 ${cx} ${cy})`} />
          );
          offset += dash;
          return seg;
        })}
        <text x={cx} y={cy - 2} textAnchor="middle" className="chart-donut__total">{total}</text>
        <text x={cx} y={cy + 14} textAnchor="middle" className="chart-donut__label">всего</text>
      </svg>
      <div className="chart-donut__legend">
        {data.map((d, i) => (
          <div key={i} className="chart-donut__legend-item">
            <span className="chart-donut__dot" style={{ background: COLOR[d.color] ?? 'var(--accent)' }} />
            <span>{d.label}</span>
            <span className="chart-donut__legend-val">{d.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Горизонтальные прогресс-полосы (освоено/законтрактовано) ──
export function ProgressBars({ items }: {
  items: { label: string; value: number; max: number; color?: string; suffix?: string }[];
}) {
  return (
    <div className="chart-bars">
      {items.map((it, i) => {
        const pct = it.max > 0 ? Math.min(100, (it.value / it.max) * 100) : 0;
        return (
          <div key={i} className="chart-bars__row">
            <div className="chart-bars__head">
              <span className="chart-bars__label">{it.label}</span>
              <span className="chart-bars__val">
                {fmtMoney(it.value)}{it.suffix ?? ' ₽'}
              </span>
            </div>
            <div className="chart-bars__track">
              <div className="chart-bars__fill"
                style={{ width: `${pct}%`, background: COLOR[it.color ?? 'accent'] ?? 'var(--accent)' }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}
